import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, Ban, Check, Copy, Info, Lock, Plus, ShieldCheck, Smartphone, Unlock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency, formatDate } from '@/lib/utils';
import { RESTORE_SETTLE_MS, settleFocus } from '@/hooks/useRestoreFocus';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { FilterChips } from '@/components/ui/filter-chips';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { AlertBanner } from '@/components/ui/alert-banner';
import { suspendReasonLabel } from '@/lib/mobile-link-labels';
import { phoneStatus, phoneText } from '@/lib/mobile-link-phone';
import { CreateLinkDialog } from './CreateLinkDialog';
import { LinkDetailsSheet, type DetailsTab } from './LinkDetailsSheet';
import { LinkSummary, useLinkSummary } from './LinkSummary';
import { buildPayload, mapServerError, similarForm, emptyForm, type CatalogEntry, type LinkForm, type MappedServerError } from './link-form';
import { STATE_TONE, creatorName, type LinkState, type MobileLinkRow } from './types';

interface Overview {
  links: { used: number; max: number | null };
  sessions: { used: number; max: number | null };
  locked: boolean;
}

interface CreatedLink {
  id: string;
  name: string;
  token: string;
  url: string | null;
  expiresAt: string;
}

interface ApiError {
  response?: { status?: number; data?: { code?: string; message?: string | string[]; threshold?: number; used?: number; max?: number } };
}

const scopeKey = (s: string) => `scope_${s.replace(/[:-]/g, '_')}`;

function errorText(err: ApiError, fallback: string): string {
  const m = err.response?.data?.message;
  return Array.isArray(m) ? m.join(', ') : (m ?? fallback);
}

/** After a link is made: read back what was just granted (same renderer as the form summary and the details drawer). */
function ReadBack({ link, form, catalog, locations }: { link: CreatedLink; form: LinkForm; catalog: CatalogEntry[]; locations: { id: string; name: string }[] }) {
  const { t } = useTranslation('mobileLinks');
  const expiresAt = new Date(link.expiresAt);
  const locationName = form.locationId ? locations.find((l) => l.id === form.locationId)?.name ?? null : null;
  const summary = useLinkSummary(form, catalog, expiresAt, locationName);
  return (
    <section aria-label={t('summaryTitle')} className="rounded-lg border p-4">
      <h3 className="mb-3 text-sm font-semibold">{t('summaryTitle')}</h3>
      <LinkSummary summary={summary} />
    </section>
  );
}

/**
 * POS-I276 / POS-I281: the phone line under the state badge in the Status cell: Active now (+ phone count) / Last seen ... / Never opened.
 * A revoked, expired or suspended link shows only the last-seen text. Hidden under 1100 px so the list fits (the drawer has the full field).
 */
function PhoneLine({ row }: { row: MobileLinkRow }) {
  const { t } = useTranslation('mobileLinks');
  const status = phoneStatus(row.state === 'active' ? row : { lastSeenAt: row.lastSeenAt });
  const text = status.kind === 'active' ? `${t('phoneActiveNow')} · ${phoneText(t, status)}` : phoneText(t, status);
  return (
    <span
      className={`mt-1 block text-xs leading-snug md:mx-auto md:max-w-[9rem] md:max-[1099px]:hidden ${status.kind === 'active' ? 'font-medium text-success-text' : 'text-muted-foreground'}`}
      title={row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleString() : undefined}
    >
      {text}
    </span>
  );
}

export function MobileLinksPage() {
  const { t } = useTranslation('mobileLinks');
  const { hasPermission } = useAuthStore();
  const canManage = hasPermission('mobile-links:manage');
  const isAdmin = hasPermission('users:update');
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  // POS-I278: the New link button gets focus back after the create flow ends (Discard, Done), when nothing else holds it.
  const newLinkRef = useRef<HTMLButtonElement>(null);
  const focusNewLinkLater = () => {
    window.setTimeout(() => { if (newLinkRef.current) settleFocus([newLinkRef.current]); }, RESTORE_SETTLE_MS);
  };
  const closeCreated = () => { setCreated(null); focusNewLinkLater(); };
  const [initialForm, setInitialForm] = useState<LinkForm>(emptyForm());
  const [dropped, setDropped] = useState<string[]>([]);
  const [serverError, setServerError] = useState<MappedServerError | null>(null);
  const [pinThreshold, setPinThreshold] = useState<number | null>(null);
  const [created, setCreated] = useState<{ link: CreatedLink; form: LinkForm } | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<MobileLinkRow | null>(null);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<'all' | LinkState>('all');
  const [lockOpen, setLockOpen] = useState(false);
  const [detailsLink, setDetailsLink] = useState<MobileLinkRow | null>(null);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('overview');

  const { data: links, isLoading } = useQuery<MobileLinkRow[]>({
    queryKey: ['mobile-links'],
    queryFn: () => api.get('/mobile-links').then((r) => r.data),
    // POS-I276: the Phone column follows the phones; refetched in the background every 30 s while the page is visible (no spinner: isLoading is the first load only).
    refetchInterval: 30_000,
  });

  const { data: overview } = useQuery<Overview>({
    queryKey: ['mobile-links-overview'],
    queryFn: () => api.get('/mobile-links/overview').then((r) => r.data),
    refetchInterval: 30_000,
  });

  const { data: catalog = [], isLoading: catalogLoading, isError: catalogError, refetch: refetchCatalog } = useQuery<CatalogEntry[]>({
    queryKey: ['mobile-links-catalog'],
    queryFn: () => api.get('/mobile-links/catalog').then((r) => r.data),
    enabled: canManage,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations-simple'],
    queryFn: () =>
      api.get('/locations').then((r) => {
        const d = r.data;
        return (Array.isArray(d) ? d : (d?.data ?? [])) as { id: string; name: string }[];
      }),
    enabled: canManage,
  });

  const createMutation = useMutation({
    mutationFn: (f: LinkForm) => api.post('/mobile-links', buildPayload(f)).then((r) => ({ link: r.data as CreatedLink, form: f })),
    onMutate: () => setServerError(null),
    onSuccess: ({ link, form }) => {
      toast.success(t('createdToast'));
      qc.invalidateQueries({ queryKey: ['mobile-links'] });
      qc.invalidateQueries({ queryKey: ['mobile-links-overview'] });
      setCreateOpen(false);
      setCopied(false);
      setCreated({ link, form });
    },
    // POS-I277: a refusal is shown inline in the dialog with translated text, never as raw API text.
    onError: (err: ApiError) => {
      const mapped = mapServerError({ status: err.response?.status, ...err.response?.data });
      if (mapped.pinThreshold != null) setPinThreshold(mapped.pinThreshold);
      setServerError(mapped);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.post(`/mobile-links/${id}/revoke`, {}),
    onSuccess: () => {
      toast.success(t('revokedToast'));
      qc.invalidateQueries({ queryKey: ['mobile-links'] });
      setRevokeTarget(null);
      focusNewLinkLater();
    },
    onError: (err: ApiError) => toast.error(errorText(err, t('revoke'))),
  });

  const revokeAllMutation = useMutation({
    mutationFn: () => api.post('/mobile-links/revoke-all', {}).then((r) => r.data as { revoked: number }),
    onSuccess: (res) => {
      toast.success(t('revokedAllToast', { count: res.revoked }));
      qc.invalidateQueries({ queryKey: ['mobile-links'] });
      setRevokeAllOpen(false);
    },
    onError: (err: ApiError) => toast.error(errorText(err, t('revokeAll'))),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['mobile-links'] });
    qc.invalidateQueries({ queryKey: ['mobile-links-overview'] });
  };

  const lockMutation = useMutation({
    mutationFn: () => api.post('/mobile-links/lockdown', {}).then((r) => r.data as { revokedLinks: number; endedSessions: number }),
    onSuccess: (res) => {
      toast.success(t('lockedToast', { links: res.revokedLinks, sessions: res.endedSessions }));
      invalidateAll();
      setLockOpen(false);
    },
    onError: (err: ApiError) => toast.error(errorText(err, t('lockdown'))),
  });

  const liftMutation = useMutation({
    mutationFn: () => api.post('/mobile-links/lockdown/lift', {}),
    onSuccess: () => {
      toast.success(t('liftedToast'));
      invalidateAll();
    },
    onError: (err: ApiError) => toast.error(errorText(err, t('lift'))),
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/mobile-links/${id}/reactivate`, { resetDevice: true }),
    onSuccess: () => {
      toast.success(t('reactivatedToast'));
      invalidateAll();
    },
    onError: (err: ApiError) => toast.error(errorText(err, t('reactivate'))),
  });

  const openCreate = (prefill?: { form: LinkForm; dropped: string[] }) => {
    setServerError(null);
    setInitialForm(prefill?.form ?? emptyForm());
    setDropped(prefill?.dropped ?? []);
    setCreateOpen(true);
  };

  const createSimilar = (l: MobileLinkRow) => {
    const r = similarForm(l, catalog, t('copySuffix'));
    setDetailsLink(null);
    openCreate(r);
  };

  const openDetails = (l: MobileLinkRow, tab: DetailsTab = 'overview') => {
    setDetailsTab(tab);
    setDetailsLink(l);
  };

  // Server public origin when configured, otherwise the address this admin is using right now.
  const linkUrl = created ? (created.link.url ?? `${window.location.origin}/m#${created.link.token}`) : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
    } catch {
      toast.error(t('copy'));
    }
  };

  const hasActive = !!links?.some((l) => l.state === 'active' || l.state === 'suspended');
  const atLinkLimit = !!overview && overview.links.max !== null && overview.links.used >= overview.links.max;
  const usageText = (m?: { used: number; max: number | null }) => (m ? `${m.used} / ${m.max ?? '∞'}` : '-');
  const countOf = (s: LinkState) => links?.filter((l) => l.state === s).length ?? 0;
  const needle = search.trim().toLowerCase();
  const visibleLinks = (links ?? []).filter((l) => {
    if (stateFilter !== 'all' && l.state !== stateFilter) return false;
    if (!needle) return true;
    const who = l.createdBy ? `${l.createdBy.firstName ?? ''} ${l.createdBy.lastName ?? ''} ${l.createdBy.email}` : '';
    return `${l.name} ${l.tokenPrefix} ${who}`.toLowerCase().includes(needle);
  });
  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        action={
          canManage && (
            <div className="flex gap-2">
              {isAdmin && (overview?.locked ? (
                <Button variant="outline" onClick={() => liftMutation.mutate()} disabled={liftMutation.isPending} className="gap-2">
                  <Unlock className="h-4 w-4" /> {t('lift')}
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setLockOpen(true)} className="gap-2 text-destructive">
                  <Lock className="h-4 w-4" /> {t('lockdown')}
                </Button>
              ))}
              {hasActive && (
                <Button variant="outline" onClick={() => setRevokeAllOpen(true)} className="gap-2">
                  <Ban className="h-4 w-4" /> {t('revokeAll')}
                </Button>
              )}
              <Button ref={newLinkRef} onClick={() => openCreate()} disabled={atLinkLimit || !!overview?.locked} title={overview?.locked ? t('lockedBanner') : atLinkLimit ? t('limitReached', { used: overview?.links.used, max: overview?.links.max }) : undefined} className="gap-2">
                <Plus className="h-4 w-4" /> {t('newLink')}
              </Button>
            </div>
          )
        }
      />

      {overview?.locked && <AlertBanner tone="danger" icon={Lock}>{t('lockedBanner')}</AlertBanner>}
      {atLinkLimit && !overview?.locked && <AlertBanner tone="warning">{t('limitReached', { used: overview?.links.used, max: overview?.links.max })}</AlertBanner>}
      {overview && (
        <p className="text-xs text-muted-foreground">
          {t('usageLine', { links: usageText(overview.links), sessions: usageText(overview.sessions) })}
        </p>
      )}

      {!!links?.length && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <FilterChips
            value={stateFilter}
            onChange={(k) => setStateFilter(k as 'all' | LinkState)}
            wrap
            chips={[
              { key: 'all', label: t('filterAll'), count: links.length },
              { key: 'active', label: t('state_active'), count: countOf('active') },
              { key: 'expired', label: t('state_expired'), count: countOf('expired') },
              { key: 'revoked', label: t('state_revoked'), count: countOf('revoked') },
              { key: 'suspended', label: t('state_suspended'), count: countOf('suspended') },
              { key: 'used_up', label: t('state_used_up'), count: countOf('used_up') },
            ]}
          />
          <SearchInput value={search} onChange={setSearch} placeholder={t('searchPlaceholder')} className="w-full sm:w-72" />
        </div>
      )}

      <Card>
        {isLoading ? (
          <SkeletonTable rows={4} cols={7} />
        ) : !links?.length ? (
          <CardContent className="pt-4">
            <EmptyState
              icon={Smartphone}
              title={t('emptyTitle')}
              description={t('emptyDesc')}
              action={canManage ? { label: t('newLink'), onClick: () => openCreate() } : undefined}
            />
          </CardContent>
        ) : visibleLinks.length === 0 ? (
          <CardContent className="pt-4">
            <EmptyState
              icon={Smartphone}
              title={t('noMatches')}
              action={{ label: t('clearFilters'), onClick: () => { setSearch(''); setStateFilter('all'); } }}
            />
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th className="px-3">{t('colName')}</Th>
                  <Th className="px-3">{t('colActivities')}</Th>
                  <Th className="hidden px-3 2xl:table-cell">{t('colCreatedBy')}</Th>
                  <Th className="px-3 md:max-xl:hidden">{t('colExpires')}</Th>
                  <Th className="px-3 md:max-[1099px]:hidden">{t('colUses')}</Th>
                  <Th className="hidden px-3 2xl:table-cell">{t('colLastUsed')}</Th>
                  <Th className="px-3 text-center">{t('colStatus')}</Th>
                  <Th className="px-3 md:sticky md:end-0 md:z-10 md:bg-card" />
                </Tr>
              </THead>
              <TBody>
                {visibleLinks.map((l) => (
                  <Tr key={l.id}>
                    <Td className="px-3 font-medium text-sm" label={t('colName')}>
                      <button
                        type="button"
                        onClick={() => openDetails(l)}
                        className="inline-block min-h-[44px] max-w-[11rem] truncate rounded md:max-xl:max-w-[7rem] align-bottom font-medium text-primary-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:min-h-0"
                        title={l.name}
                      >
                        {l.name}
                      </button>
                      <span className="ms-2 font-mono text-xs text-muted-foreground md:max-xl:hidden">{l.tokenPrefix}…</span>
                    </Td>
                    <Td className="px-3" label={t('colActivities')}>
                      <div className="flex max-h-[3.25rem] max-w-[13rem] flex-wrap gap-1 overflow-hidden md:max-xl:max-h-[1.75rem] md:max-xl:max-w-[8rem]" title={l.scopes.filter((s) => s !== 'pos:discount').map((s) => t(scopeKey(s), s)).join(', ')}>
                        {l.scopes.map((s) => (
                          s === 'pos:discount' ? null : <Badge key={s} variant="info">{t(scopeKey(s), s)}</Badge>
                        ))}
                        {l.scopes.includes('pos:discount') && l.maxDiscountPercent != null && (
                          <Badge variant="info">
                            {l.maxDiscountAmount != null
                              ? t('badgeDiscountAmount', { pct: l.maxDiscountPercent, amount: formatCurrency(l.maxDiscountAmount) })
                              : t('badgeDiscount', { pct: l.maxDiscountPercent })}
                          </Badge>
                        )}
                      </div>
                    </Td>
                    <Td className="hidden px-3 text-sm 2xl:table-cell" label={t('colCreatedBy')}>{creatorName(l)}</Td>
                    <Td className="whitespace-nowrap px-3 text-sm text-muted-foreground md:max-xl:hidden" label={t('colExpires')}>{formatDate(l.expiresAt)}</Td>
                    <Td className="px-3 text-sm md:max-[1099px]:hidden" label={t('colUses')}>
                      {l.useCount} / {l.maxUses ?? t('unlimited')}
                    </Td>
                    <Td className="hidden px-3 text-sm text-muted-foreground 2xl:table-cell" label={t('colLastUsed')}>
                      {l.lastUsedAt ? formatDate(l.lastUsedAt) : t('never')}
                    </Td>
                    <Td className="px-3 text-center" label={t('colStatus')}>
                      <Badge variant={STATE_TONE[l.state]}>{t(`state_${l.state}`)}</Badge>
                      <PhoneLine row={l} />
                      {l.state === 'suspended' && l.suspendedReason && (
                        <span className="mt-1 block text-xs text-muted-foreground">{suspendReasonLabel(t, l.suspendedReason)}</span>
                      )}
                    </Td>
                    <Td className="td-actions px-3 md:sticky md:end-0 md:z-10 md:bg-card">
                      <TableRowActions
                        actions={[
                          {
                            key: 'details',
                            label: t('detailsAction'),
                            icon: Info,
                            onSelect: () => openDetails(l),
                            permission: true,
                          },
                          {
                            key: 'activity',
                            label: t('activity'),
                            icon: Activity,
                            onSelect: () => openDetails(l, 'activity'),
                            permission: true,
                          },
                          {
                            key: 'reactivate',
                            label: t('reactivate'),
                            icon: ShieldCheck,
                            onSelect: () => reactivateMutation.mutate(l.id),
                            permission: isAdmin && l.state === 'suspended',
                          },
                          {
                            key: 'revoke',
                            label: t('revoke'),
                            icon: Ban,
                            onSelect: () => setRevokeTarget(l),
                            permission: canManage && l.state !== 'revoked',
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>

      <CreateLinkDialog
        open={createOpen}
        initialForm={initialForm}
        dropped={dropped}
        catalog={catalog}
        catalogLoading={catalogLoading && canManage}
        catalogError={catalogError}
        onRetryCatalog={() => { void refetchCatalog(); }}
        locations={locations}
        isPending={createMutation.isPending}
        serverError={serverError}
        pinThreshold={pinThreshold}
        onSubmit={(f) => createMutation.mutate(f)}
        onClose={() => { setCreateOpen(false); setServerError(null); }}
      />

      <FormDialog
        open={!!created}
        title={t('linkReadyTitle')}
        width="560px"
        submitLabel={t('close')}
        onSubmit={(e) => { e.preventDefault(); closeCreated(); }}
        onClose={closeCreated}
      >
        {created && (
          <div className="space-y-4">
            <p className="text-sm text-warning-text">{t('linkReadyWarn')}</p>
            <div className="flex justify-center rounded-lg p-4" style={{ background: "#fff" }}>
              <QRCodeSVG value={linkUrl} size={200} />
            </div>
            <p className="text-center text-xs text-muted-foreground">{t('scanToOpen')}</p>
            <div className="flex gap-2">
              <Input readOnly value={linkUrl} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
              <Button type="button" variant="outline" onClick={copyLink} className="gap-2 shrink-0">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('copied') : t('copy')}
              </Button>
            </div>
            <ReadBack link={created.link} form={created.form} catalog={catalog} locations={locations} />
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={!!revokeTarget}
        title={t('revokeTitle')}
        message={t('revokeBody', { name: revokeTarget?.name ?? '' })}
        confirmLabel={t('revoke')}
        loading={revokeMutation.isPending}
        onConfirm={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
        onCancel={() => { setRevokeTarget(null); focusNewLinkLater(); }}
      />

      <ConfirmDialog
        open={lockOpen}
        title={t('lockdownTitle')}
        message={t('lockdownBody')}
        confirmLabel={t('lockdown')}
        loading={lockMutation.isPending}
        onConfirm={() => lockMutation.mutate()}
        onCancel={() => setLockOpen(false)}
      />

      <LinkDetailsSheet
        link={detailsLink}
        tab={detailsTab}
        onTab={setDetailsTab}
        onClose={() => setDetailsLink(null)}
        catalog={catalog}
        locations={locations}
        isAdmin={isAdmin}
        canManage={canManage}
        onRevoke={(l) => { setDetailsLink(null); setRevokeTarget(l); }}
        onReactivate={(l) => { reactivateMutation.mutate(l.id); setDetailsLink(null); }}
        onCreateSimilar={createSimilar}
      />

      <ConfirmDialog
        open={revokeAllOpen}
        title={t('revokeAllTitle')}
        message={t('revokeAllBody')}
        confirmLabel={t('revokeAll')}
        loading={revokeAllMutation.isPending}
        onConfirm={() => revokeAllMutation.mutate()}
        onCancel={() => setRevokeAllOpen(false)}
      />
    </div>
  );
}
